/**
 * Security Utilities for KAISITE Theme
 * Provides safe HTML sanitization to prevent XSS attacks
 */

// Simple HTML sanitizer for trusted content from Shopify API
class HTMLSanitizer {
  constructor() {
    // Whitelist of allowed tags (extend as needed)
    this.allowedTags = new Set([
      'div', 'span', 'p', 'a', 'strong', 'em', 'b', 'i', 'u',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'br', 'hr',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'img', 'figure', 'figcaption',
      'form', 'input', 'button', 'select', 'option', 'textarea', 'label',
      'article', 'section', 'header', 'footer', 'nav', 'aside',
      'svg', 'path', 'circle', 'rect', 'polygon', 'line', 'polyline',
    ]);

    // Whitelist of allowed attributes
    this.allowedAttributes = new Set([
      'class', 'id', 'href', 'title', 'alt', 'src', 'data-*',
      'aria-*', 'role', 'name', 'type', 'value', 'placeholder',
      'disabled', 'checked', 'selected', 'for', 'form',
      'width', 'height', 'colspan', 'rowspan',
      'fill', 'stroke', 'd', 'cx', 'cy', 'r', 'x', 'y',
    ]);

    // Dangerous attributes to always remove (used as fallback; strictSanitize uses a regex to block ALL on* events)
    this.dangerousAttributes = new Set([
      'onclick', 'onerror', 'onload', 'onmouseover', 'onfocus', 'onblur',
      'onchange', 'onsubmit', 'onkeydown', 'onkeyup', 'onkeypress',
    ]);
  }

  /**
   * Sanitize HTML string by removing dangerous elements and attributes
   * @param {string} html - HTML string to sanitize
   * @param {Object} options - Sanitization options
   * @returns {string} - Sanitized HTML
   */
  sanitize(html, options = {}) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    // For Shopify-rendered HTML from API responses, we can trust the source
    // But still apply basic protection
    if (options.trustShopifySource === true) {
      return this.sanitizeShopifyHTML(html);
    }

    // For user-generated content, apply strict sanitization
    return this.strictSanitize(html);
  }

  /**
   * Light sanitization for trusted Shopify API responses
   * @param {string} html - HTML from Shopify
   * @returns {string}
   */
  sanitizeShopifyHTML(html) {
    const cleaned = html
      // Remove script tags
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // Remove javascript: URIs
      .replace(/javascript:/gi, '')
      // Remove dangerous data: URIs (text/html, application/xhtml) that can execute code — handles encoding tricks
      .replace(/data:\s*text(?:\/|%2f|%2F)html/gi, 'about:blank')
      .replace(/data:\s*application(?:\/|%2f|%2F)xhtml/gi, 'about:blank')
      // Remove event handler attributes (on*) — handles whitespace including newlines/tabs before =
      .replace(/\bon[a-z]+[\s\n\t]*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, 'data-blocked=')
      // Remove dangerous elements
      .replace(/<base\b[^>]*\/?>/gi, '')
      .replace(/<meta\b[^>]*http-equiv\s*=\s*["']?refresh["']?[^>]*\/?>/gi, '')
      .replace(/<link\b[^>]*rel\s*=\s*["']?import["']?[^>]*\/?>/gi, '')
      // Remove object/embed/applet tags
      .replace(/<object\b[^>]*>.*?<\/object>/gis, '')
      .replace(/<embed\b[^>]*\/?>/gi, '')
      .replace(/<applet\b[^>]*>.*?<\/applet>/gis, '')
      // Remove srcdoc attributes that could inject HTML
      .replace(/\bsrcdoc\s*=\s*(?:"[^"]*"|'[^']*')/gi, '');

    return cleaned;
  }

  /**
   * Strict sanitization for untrusted content
   * @param {string} html - Untrusted HTML
   * @returns {string}
   */
  strictSanitize(html) {
    // Create a temporary element
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Remove all script tags and dangerous embedded elements
    temp.querySelectorAll('script, object, embed, applet, base').forEach(el => el.remove());

    // Process all elements
    const allElements = temp.querySelectorAll('*');
    allElements.forEach(element => {
      // Remove dangerous attributes
      Array.from(element.attributes).forEach(attr => {
        const name = attr.name.toLowerCase();
        const value = (attr.value || '').toLowerCase();
        // Remove ALL event handlers (on* attributes)
        if (/^on[a-z]/.test(name)) {
          element.removeAttribute(attr.name);
          return;
        }
        // Remove javascript: URIs
        if (value.includes('javascript:')) {
          element.removeAttribute(attr.name);
          return;
        }
        // Remove data:text/html URIs (including encoded variants)
        if (value.startsWith('data:') && /text\/html|text%2fhtml|application\/xhtml/i.test(value)) {
          element.removeAttribute(attr.name);
          return;
        }
        // Remove srcdoc attributes
        if (name === 'srcdoc') {
          element.removeAttribute(attr.name);
          return;
        }
      });
    });

    return temp.innerHTML;
  }

  /**
   * Escape HTML entities for text content
   * @param {string} text - Text to escape
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Safe alternative to innerHTML for text content
   * @param {HTMLElement} element - Target element
   * @param {string} text - Text content
   */
  setTextContent(element, text) {
    if (element) {
      element.textContent = text;
    }
  }

  /**
   * Safe alternative to innerHTML for trusted Shopify HTML
   * @param {HTMLElement} element - Target element
   * @param {string} html - HTML from Shopify API
   */
  setShopifyHTML(element, html) {
    if (element && html) {
      element.innerHTML = this.sanitizeShopifyHTML(html);
    }
  }
}

// Create singleton instance
const sanitizer = new HTMLSanitizer();

// Export utility functions
window.SecureHTML = {
  sanitize: (html, options) => sanitizer.sanitize(html, options),
  escape: (text) => sanitizer.escapeHtml(text),
  setText: (element, text) => sanitizer.setTextContent(element, text),
  setShopifyHTML: (element, html) => sanitizer.setShopifyHTML(element, html),
};

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HTMLSanitizer;
}
